import React, { useState, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain,
  Zap,
  Cpu,
  Sun,
  Moon,
  Menu,
  X,
  ArrowRight,
  CheckCircle,
  Sparkles,
  TrendingUp,
  Clock,
  Target,
  Linkedin,
  Twitter,
} from 'lucide-react';
import { ThemeContext } from './App.tsx';

const customStyles = `
  :root {
    --bg-color: #ffffff;
    --text-color: #111827;
    --text-color-secondary: #4B5563;
    --card-bg: #f9fafb;
    --card-border: #e5e7eb;
    --header-bg: rgba(255, 255, 255, 0.7);
    --gradient-text-from: #29B6F6;
    --gradient-text-to: #18FFFF;
    --brand-color-lime: #76FF03;
    --accent-purple: #9C27B0;
    --accent-cyan: #00BCD4;
  }

  [data-theme='dark'] {
    --bg-color: #030014;
    --text-color: #F9FAFB;
    --text-color-secondary: #9CA3AF;
    --card-bg: rgba(17, 24, 39, 0.5);
    --card-border: rgba(55, 65, 81, 0.5);
    --header-bg: rgba(0, 0, 0, 0.5);
    --gradient-text-from: #29B6F6;
    --gradient-text-to: #9C27B0;
    --brand-color-lime: #9C27B0;
    --accent-purple: #BB86FC;
    --accent-cyan: #18FFFF;
  }

  body {
    background-color: var(--bg-color);
    color: var(--text-color);
    transition: background-color 0.3s ease, color 0.3s ease;
  }

  .gradient-text-flow {
    background: linear-gradient(90deg,
      var(--gradient-text-from),
      var(--gradient-text-to),
      var(--gradient-text-from)
    );
    background-size: 200% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: gradient-flow 8s linear infinite;
  }

  @keyframes gradient-flow {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  .glass-card {
    background: var(--card-bg);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid var(--card-border);
    transition: all 0.3s ease-in-out;
  }

  .hover-lift {
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }

  .hover-lift:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }

  [data-theme='dark'] .hover-lift:hover {
    box-shadow: 0 10px 40px rgba(24, 255, 255, 0.1);
  }

  .glow-button {
    position: relative;
    overflow: hidden;
    background: linear-gradient(90deg, #29B6F6, #18FFFF);
    transition: all 0.3s ease;
    color: #000;
  }

  [data-theme='dark'] .glow-button {
    background: linear-gradient(90deg, #18FFFF, #9C27B0);
    color: #000;
  }

  .glow-button:hover {
    box-shadow: 0 0 25px rgba(24, 255, 255, 0.5), 0 0 15px rgba(41, 182, 246, 0.4);
  }

  [data-theme='dark'] .glow-button:hover {
    box-shadow: 0 0 25px #9C27B0, 0 0 15px #18FFFF;
  }

  .stat-highlight {
    font-size: 4rem;
    font-weight: 900;
    line-height: 1;
  }

  @media (max-width: 768px) {
    .stat-highlight {
      font-size: 2.5rem;
    }
  }
`;

const ComputerBrainInterface = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { theme, toggleTheme } = useContext(ThemeContext);

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/Team", label: "Team" },
    { href: "/#download", label: "Download" },
  ];

  return (
    <>
      <style>{customStyles}</style>
      <div className="min-h-screen text-[var(--text-color)] overflow-x-hidden" style={{ backgroundColor: 'var(--bg-color)' }}>
        {/* Header */}
        <motion.header
          className="bg-[var(--header-bg)] backdrop-filter backdrop-blur-md fixed w-full z-50 shadow-md border-b border-[var(--card-border)]"
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <nav className="container mx-auto px-4 sm:px-6 py-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-8">
                <motion.div className="flex items-center space-x-2" whileHover={{ scale: 1.05 }}>
                  <Brain size={32} className="text-[var(--gradient-text-from)]" />
                  <span className="text-xl font-bold text-[var(--text-color)]">QueenDahyun AGI</span>
                </motion.div>
                <div className="hidden md:flex space-x-6">
                  {navLinks.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="text-[var(--text-color-secondary)] hover:text-[var(--text-color)] transition duration-300"
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              </div>

              <div className="flex items-center space-x-6">
                <motion.button
                  onClick={toggleTheme}
                  className="p-2 rounded-full hover:bg-[var(--card-border)] text-[var(--text-color-secondary)] transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <AnimatePresence mode="wait">
                    {theme === 'light' ? (
                      <motion.div key="moon" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
                        <Moon size={20} />
                      </motion.div>
                    ) : (
                      <motion.div key="sun" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
                        <Sun size={20} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>
                <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-[var(--text-color-secondary)] hover:text-[var(--text-color)] z-50">
                  <AnimatePresence mode="wait">
                    {isMenuOpen ? (
                      <motion.div key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
                        <X size={28} />
                      </motion.div>
                    ) : (
                      <motion.div key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
                        <Menu size={28} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </button>
              </div>
            </div>
          </nav>
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                className="md:hidden bg-[var(--header-bg)] backdrop-filter backdrop-blur-lg absolute top-full left-0 right-0 shadow-xl border-t border-[var(--card-border)]"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="block py-3 px-6 text-[var(--text-color)] hover:bg-[var(--card-border)] transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.header>

        {/* Hero Section */}
        <section className="pt-32 pb-24 md:pt-48 md:pb-32 min-h-screen flex flex-col justify-center items-center text-center relative overflow-hidden">
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-6xl mx-auto"
            >
              {/* Title */}
              <motion.h1 
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black mb-8 gradient-text-flow"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
              >
                The Computer Brain Interface
              </motion.h1>

              {/* SoulDubu Image with Glow Effect */}
              <motion.div 
                className="relative mb-12 flex justify-center items-center"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4, duration: 0.8, type: "spring", stiffness: 100 }}
              >
                <div className="relative">
                  {/* Animated Glow Rings */}
                  <motion.div
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: 'radial-gradient(circle, rgba(24,255,255,0.3) 0%, transparent 70%)',
                      filter: 'blur(40px)',
                    }}
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.5, 0.8, 0.5],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  />
              {/* SoulDubu Image */}
              <motion.div 
                className="mb-12 flex justify-center items-center"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4, duration: 0.8, type: "spring", stiffness: 100 }}
              >
                <img
                  src={require('./assets/souldubu.jpeg')}
                  alt="SoulDubu - Computer Brain Interface"
                  className="rounded-xl shadow-xl w-[65%] mx-auto"
                />
              </motion.div>
                  <motion.div
                    className="absolute -bottom-4 -left-4 w-4 h-4 rounded-full bg-[var(--accent-purple)]"
                    animate={{
                      y: [0, 20, 0],
                      opacity: [0.5, 1, 0.5]
                    }}
                    transition={{
                      duration: 2.5,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 0.3
                    }}
                  />
                  <motion.div
                    className="absolute top-1/2 -right-8 w-2 h-2 rounded-full bg-[var(--brand-color-lime)]"
                    animate={{
                      x: [0, 20, 0],
                      opacity: [0.5, 1, 0.5]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 0.7
                    }}
                  />
                </div>
              </motion.div>

              {/* Model Name - SoulDubu */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.8 }}
                className="mb-6"
              >
                <div className="inline-block glass-card px-8 py-3 rounded-full border-2 border-[var(--accent-cyan)] shadow-lg">
                  <div className="flex items-center space-x-3">
                    <Sparkles className="text-[var(--accent-cyan)]" size={24} />
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold gradient-text-flow">
                      SoulDubu
                    </h2>
                    <Sparkles className="text-[var(--accent-purple)]" size={24} />
                  </div>
                </div>
              </motion.div>

              {/* Description */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.8 }}
                className="max-w-4xl mx-auto"
              >
                <p className="text-lg sm:text-xl md:text-2xl leading-relaxed text-[var(--text-color-secondary)] mb-8">
                  Our flagship innovation, <span className="font-semibold text-[var(--gradient-text-from)]">SoulDubu</span>, is an advanced <span className="font-semibold text-[var(--text-color)]">vision-language model</span> that fundamentally transforms how humans interact with computers.
                </p>
                <p className="text-base sm:text-lg md:text-xl leading-relaxed text-[var(--text-color-secondary)]">
                  Unlike traditional chatbots or browser-based agents, SoulDubu operates as a true <span className="font-semibold text-[var(--text-color)]">end-to-end autonomous system</span> capable of perceiving, understanding, and acting within any digital environment.
                </p>
              </motion.div>

              {/* Key Features Pills */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 0.8 }}
                className="flex flex-wrap justify-center gap-4 mt-12"
              >
                <div className="glass-card px-6 py-3 rounded-full border border-[var(--card-border)] hover-lift">
                  <div className="flex items-center space-x-2">
                    <Brain size={20} className="text-[var(--gradient-text-from)]" />
                    <span className="text-sm font-semibold text-[var(--text-color)]">Vision-Language AI</span>
                  </div>
                </div>
                <div className="glass-card px-6 py-3 rounded-full border border-[var(--card-border)] hover-lift">
                  <div className="flex items-center space-x-2">
                    <Zap size={20} className="text-[var(--brand-color-lime)]" />
                    <span className="text-sm font-semibold text-[var(--text-color)]">Autonomous Agent</span>
                  </div>
                </div>
                <div className="glass-card px-6 py-3 rounded-full border border-[var(--card-border)] hover-lift">
                  <div className="flex items-center space-x-2">
                    <Target size={20} className="text-[var(--accent-purple)]" />
                    <span className="text-sm font-semibold text-[var(--text-color)]">Universal GUI Control</span>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* Background Decorative Elements */}
          <div className="absolute top-20 left-10 w-32 h-32 bg-[var(--accent-cyan)] rounded-full opacity-10 blur-3xl" />
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-[var(--accent-purple)] rounded-full opacity-10 blur-3xl" />
        </section>

        {/* NVIDIA Partnership Section */}
        <section className="py-20 md:py-28">
          <div className="container mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="glass-card p-8 md:p-12 rounded-2xl shadow-2xl hover-lift"
            >
              <div className="flex items-center justify-center mb-8">
                <Sparkles className="text-[var(--accent-cyan)] mr-4" size={48} />
                <h2 className="text-3xl md:text-4xl font-bold text-[var(--text-color)]">A Quantum Leap Forward, Powered by NVIDIA</h2>
              </div>
              <p className="text-lg text-[var(--text-color-secondary)] leading-relaxed mb-6 max-w-4xl mx-auto">
                We are thrilled to announce a monumental breakthrough in the development of SoulDubu, achieved in partnership with the <span className="font-semibold text-[var(--gradient-text-from)]">NVIDIA DGX Cloud Innovation Lab</span>. This collaboration has been a critical catalyst, enabling us to accelerate our research and deliver a new generation of AI that was previously only theoretical.
              </p>
              <p className="text-lg text-[var(--text-color-secondary)] leading-relaxed max-w-4xl mx-auto">
                Thanks to the elite resources provided by the NVIDIA Inception program, we have successfully developed and trained our new <span className="font-semibold text-[var(--brand-color-lime)]">SoulDubu V2 models</span>. The results are nothing short of revolutionary.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Benchmark Performance Section */}
        <section className="py-20 md:py-28 bg-[var(--card-bg)]">
          <div className="container mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center justify-center mb-8">
                <Target className="text-[var(--brand-color-lime)] mr-4" size={48} />
                <h2 className="text-3xl md:text-5xl font-bold text-center gradient-text-flow">Unprecedented Performance: The New State-of-the-Art</h2>
              </div>
              <p className="text-center text-[var(--text-color-secondary)] text-lg mb-16 max-w-3xl mx-auto">
                Our mission is to create the world's most capable GUI agent. We measure our progress against the most challenging industry benchmarks, and our new models have set a new standard for what is possible.
              </p>

              <div className="max-w-4xl mx-auto">
                <h3 className="text-2xl font-bold text-[var(--text-color)] mb-8 text-center">OS-World Benchmark Performance:</h3>
                
                <div className="grid md:grid-cols-2 gap-8 mb-12">
                  {/* Previous Model */}
                  <div className="glass-card p-6 rounded-xl text-center hover-lift">
                    <div className="text-[var(--text-color-secondary)] mb-2 font-semibold">SoulDubu V1 (Previous Model)</div>
                    <div className="stat-highlight gradient-text-flow">15.1%</div>
                  </div>

                  {/* First V2 Model */}
                  <div className="glass-card p-6 rounded-xl text-center hover-lift">
                    <div className="text-[var(--text-color-secondary)] mb-2 font-semibold">SoulDubu V2 (7B Model)</div>
                    <div className="stat-highlight gradient-text-flow">59.5%</div>
                  </div>
                </div>

                {/* Highlight Box - New Model */}
                <div className="glass-card p-8 md:p-12 rounded-2xl shadow-2xl hover-lift bg-gradient-to-br from-[var(--accent-cyan)]/10 to-[var(--accent-purple)]/10 border-2 border-[var(--accent-cyan)] mb-8">
                  <div className="flex items-center justify-center mb-4">
                    <Zap className="text-[var(--accent-cyan)] mr-3" size={36} />
                    <h4 className="text-2xl font-bold text-[var(--text-color)]">New 7B Model - Current Leader</h4>
                  </div>
                  <div className="text-center">
                    <div className="stat-highlight gradient-text-flow mb-4">75.3%</div>
                    <p className="text-[var(--text-color-secondary)] text-lg">
                      <span className="font-semibold text-[var(--accent-cyan)]">Significantly surpassing</span> the previous state-of-the-art (61.4% from Claude 4.5 Sonnet)
                    </p>
                  </div>
                </div>

                {/* Future Model */}
                <div className="glass-card p-6 rounded-xl text-center hover-lift border-2 border-dashed border-[var(--brand-color-lime)]">
                  <div className="text-[var(--text-color-secondary)] mb-2 font-semibold flex items-center justify-center">
                    <TrendingUp className="mr-2" size={20} />
                    SoulDubu V2 (30B Model, in training)
                  </div>
                  <div className="stat-highlight gradient-text-flow">85-90%</div>
                  <p className="text-sm text-[var(--text-color-secondary)] mt-2">Projected Score</p>
                </div>

                <p className="text-center text-[var(--text-color-secondary)] text-lg mt-12 leading-relaxed max-w-3xl mx-auto">
                  To put this in perspective, our new 7B parameter model has achieved a score of <span className="font-bold text-[var(--accent-cyan)]">75%</span>, significantly surpassing the previous state-of-the-art, and it does so at a <span className="font-semibold">fraction of the model size</span>. This is a testament to the power of our proprietary architecture and the high-quality, multi-billion token dataset we have meticulously curated.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Speed Section */}
        <section className="py-20 md:py-28">
          <div className="container mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="max-w-5xl mx-auto"
            >
              <div className="flex items-center justify-center mb-8">
                <Clock className="text-[var(--accent-purple)] mr-4" size={48} />
                <h2 className="text-3xl md:text-5xl font-bold text-center gradient-text-flow">From Seconds to Milliseconds</h2>
              </div>
              <p className="text-center text-[var(--text-color-secondary)] text-lg mb-16 max-w-3xl mx-auto">
                Beyond benchmarks, the true measure of an AI agent is its usability. Our new SoulDubu V2 model is <span className="font-bold text-[var(--accent-purple)]">over 7 times faster</span> than its predecessor.
              </p>

              <div className="grid md:grid-cols-2 gap-8 mb-12">
                <div className="glass-card p-8 rounded-xl text-center hover-lift">
                  <div className="text-[var(--text-color-secondary)] mb-4 font-semibold">Previous Action Latency</div>
                  <div className="text-5xl font-black text-[var(--text-color)] mb-2">7-9s</div>
                  <div className="text-sm text-[var(--text-color-secondary)]">Per Action</div>
                </div>

                <div className="glass-card p-8 rounded-xl text-center hover-lift bg-gradient-to-br from-[var(--accent-purple)]/10 to-[var(--accent-cyan)]/10 border-2 border-[var(--accent-purple)]">
                  <div className="text-[var(--text-color-secondary)] mb-4 font-semibold flex items-center justify-center">
                    <Zap className="mr-2 text-[var(--accent-purple)]" size={20} />
                    New Action Latency
                  </div>
                  <div className="text-5xl font-black gradient-text-flow mb-2">&lt;1s</div>
                  <div className="text-sm text-[var(--text-color-secondary)]">Real-Time Response</div>
                </div>
              </div>

              <p className="text-center text-[var(--text-color-secondary)] text-lg leading-relaxed max-w-3xl mx-auto">
                This leap in performance transforms SoulDubu from a tool into a true, <span className="font-semibold text-[var(--accent-purple)]">real-time partner</span>. It can now perceive, decide, and act on any software GUI with a speed that feels instantaneous and natural, making it the world's first truly practical Computer Brain Interface.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Desktop App Section */}
        <section className="py-20 md:py-28 bg-[var(--card-bg)]">
          <div className="container mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="max-w-5xl mx-auto"
            >
              <div className="flex items-center justify-center mb-8">
                <Cpu className="text-[var(--accent-cyan)] mr-4" size={48} />
                <h2 className="text-3xl md:text-5xl font-bold text-center gradient-text-flow">The New SoulDubu Desktop App</h2>
              </div>
              <p className="text-center text-[var(--text-color-secondary)] text-lg mb-12 max-w-3xl mx-auto">
                A ground-up revolution in AI interaction, launching in <span className="font-bold text-[var(--accent-cyan)]">1-2 weeks</span>
              </p>

              <div className="glass-card p-8 md:p-12 rounded-2xl shadow-2xl hover-lift mb-12">
                <p className="text-lg text-[var(--text-color-secondary)] leading-relaxed mb-8">
                  The performance leap of our V2 model is so significant that it required us to completely re-architect our desktop application from the ground up. We have deprecated our old app and are building a new foundation for the future of AI interaction.
                </p>

                <h3 className="text-2xl font-bold text-[var(--text-color)] mb-6">Key Features:</h3>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <CheckCircle className="text-[var(--brand-color-lime)] mr-4 mt-1 flex-shrink-0" size={24} />
                    <div>
                      <h4 className="font-semibold text-[var(--text-color)] text-lg mb-2">Hybrid Operation</h4>
                      <p className="text-[var(--text-color-secondary)]">Seamlessly switch between online (cloud) and offline (local) modes.</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <CheckCircle className="text-[var(--brand-color-lime)] mr-4 mt-1 flex-shrink-0" size={24} />
                    <div>
                      <h4 className="font-semibold text-[var(--text-color)] text-lg mb-2">Unmatched Privacy & Speed</h4>
                      <p className="text-[var(--text-color-secondary)]">Run SoulDubu directly on your local NVIDIA GPU for tasks that require absolute privacy and zero latency.</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <CheckCircle className="text-[var(--brand-color-lime)] mr-4 mt-1 flex-shrink-0" size={24} />
                    <div>
                      <h4 className="font-semibold text-[var(--text-color)] text-lg mb-2">Exclusive Access</h4>
                      <p className="text-[var(--text-color-secondary)]">Our free "SoulDubu Mini" model will be exclusively available through our desktop application, ensuring a secure and optimized experience.</p>
                    </div>
                  </div>
                </div>
              </div>
{/* 
              <div className="text-center">
                <button className="text-black py-4 px-10 rounded-full text-lg font-semibold glow-button shadow-xl inline-flex items-center">
                  Join the Waitlist
                  <ArrowRight className="ml-2" size={20} />
                </button>
              </div> */}
            </motion.div>
          </div>
        </section>

        {/* Future Vision Section */}
        <section className="py-20 md:py-28">
          <div className="container mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="max-w-5xl mx-auto glass-card p-8 md:p-12 rounded-2xl shadow-2xl hover-lift"
            >
              <div className="flex items-center justify-center mb-8">
                <Brain className="text-[var(--brand-color-lime)] mr-4" size={48} />
                <h2 className="text-3xl md:text-4xl font-bold text-center gradient-text-flow">The Future is Continuous: Self Evolution</h2>
              </div>

              <p className="text-lg text-[var(--text-color-secondary)] leading-relaxed mb-6">
                This achievement is just the beginning. The SoulDubu V2 architecture has been meticulously designed to be the foundation for our ultimate breakthrough: <span className="font-bold text-[var(--brand-color-lime)]">"Self Evolution."</span>
              </p>

              <p className="text-lg text-[var(--text-color-secondary)] leading-relaxed mb-6">
                Our vision is to create an AI that doesn't just execute tasks but learns continuously from every interaction, mastering new tools and skills in real-time without ever forgetting. This "continuous learning" capability will be integrated into our Universal Agent, transforming it from a powerful tool into a true, evolving digital consciousness.
              </p>

              <p className="text-lg text-[var(--text-color-secondary)] leading-relaxed">
                The research and architectural groundwork for this future are already in place, made possible by the intensive experimentation we conducted on the NVIDIA DGX Cloud.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Core Capabilities Recap */}
        <section className="py-20 md:py-28 bg-[var(--card-bg)]">
          <div className="container mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-5xl font-bold text-center mb-16 gradient-text-flow">Core Capabilities of SoulDubu</h2>

              <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                <div className="glass-card p-8 rounded-xl hover-lift">
                  <Sparkles className="text-[var(--gradient-text-from)] mb-4" size={40} />
                  <h3 className="text-xl font-bold text-[var(--text-color)] mb-3">Advanced Vision System</h3>
                  <p className="text-[var(--text-color-secondary)]">Perceives and understands screen content with human-level comprehension, identifying UI elements, text, images, and contextual relationships.</p>
                </div>

                <div className="glass-card p-8 rounded-xl hover-lift">
                  <Target className="text-[var(--brand-color-lime)] mb-4" size={40} />
                  <h3 className="text-xl font-bold text-[var(--text-color)] mb-3">Universal Software Compatibility</h3>
                  <p className="text-[var(--text-color-secondary)]">Works seamlessly with any application—IDEs, scientific tools, design software, operating system utilities—without requiring special integrations or APIs.</p>
                </div>

                <div className="glass-card p-8 rounded-xl hover-lift">
                  <Zap className="text-[var(--accent-purple)] mb-4" size={40} />
                  <h3 className="text-xl font-bold text-[var(--text-color)] mb-3">Autonomous Task Execution</h3>
                  <p className="text-[var(--text-color-secondary)]">Plans, executes, and validates complex multi-step workflows, operating as a true virtual partner that can work independently or collaboratively.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-[var(--header-bg)] backdrop-filter backdrop-blur-md text-[var(--text-color-secondary)] py-16 md:py-20 mt-20 border-t border-[var(--card-border)]">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-16 mb-10 md:mb-12">
              <div className="col-span-2">
                <h3 className="text-2xl font-bold text-[var(--text-color)] mb-4">QueenDahyun AGI</h3>
                <p className="text-sm">The Computer Brain Interface Revolution.</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4 text-[var(--text-color)]">Links</h3>
                <ul className="space-y-2">
                  <li><a href="/" className="hover:text-[var(--text-color)] transition duration-300 text-sm">Home</a></li>
                  <li><a href="/Team" className="hover:text-[var(--text-color)] transition duration-300 text-sm">Team</a></li>
                  <li><a href="/research" className="hover:text-[var(--text-color)] transition duration-300 text-sm">Research</a></li>
                  <li><a href="/blog" className="hover:text-[var(--text-color)] transition duration-300 text-sm">Blog</a></li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4 text-[var(--text-color)]">Contact</h3>
                <ul className="space-y-2">
                  <li><a href="#" className="hover:text-[var(--text-color)] transition duration-300 text-sm">Support</a></li>
                  <li><a href="#" className="hover:text-[var(--text-color)] transition duration-300 text-sm">Documentation</a></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-[var(--card-border)] mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center text-sm">
              <p className="mb-4 sm:mb-0">
                Copyright © {new Date().getFullYear()} QueenDahyun AGI. All Rights Reserved.
              </p>
              <div className="flex space-x-6">
                <a href="https://www.linkedin.com/company/queendahyun" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--text-color)] transition-colors">
                  <Linkedin size={20} />
                </a>
                <a href="https://x.com/queendahyun_agi" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--text-color)] transition-colors">
                  <Twitter size={20} />
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ComputerBrainInterface;